import React from "react";
import { useTranslation } from "react-i18next";
import { Box, IconButton, Skeleton, Typography, styled } from "@mui/material";
import { ApiTokenResponse } from "@rentablo/api/apiv1";
import DeleteIcon from "@icons/myicons/trash-bin-delete.svg?react";
import { formatDateDDMMYYYY } from "@helpers";
import { PermissionBadges } from "./PermissionBadges";
import { TokenExpiryCell } from "./TokenExpiryCell";
import { formatLastUsed } from "./helpers";

const Row = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  padding: theme.spacing(3, 0),
  borderBottom: `1px solid ${theme.palette.border.secondary}`,
  "&:first-of-type": {
    borderTop: `1px solid ${theme.palette.border.secondary}`,
  },
}));

const Line = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(3),
  minWidth: 0,
}));

interface Props {
  tokens: ApiTokenResponse[];
  isPending: boolean;
  onRevoke: (token: ApiTokenResponse) => void;
}

export const ApiTokenMobileList = ({ tokens, isPending, onRevoke }: Props) => {
  const { t } = useTranslation();

  if (isPending) {
    return (
      <Box>
        {Array.from({ length: 3 }, (_, index) => (
          <Skeleton key={index} variant="rounded" height={72} sx={{ my: 3 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box data-testid="api-token-mobile-list">
      {tokens.map((token) => (
        <Row key={token.id} data-testid="api-token-mobile-row">
          <Line>
            <Box minWidth={0}>
              <Typography variant="body4" component="div" noWrap>
                {token.name}
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                component="div"
                noWrap
              >
                {t("settings.apiAccess.table.id")} {token.id} ·{" "}
                {t("settings.apiAccess.table.createdAt", {
                  date: formatDateDDMMYYYY(token.createdAt),
                })}
              </Typography>
            </Box>
            <IconButton
              size="small"
              aria-label={t("settings.apiAccess.table.revokeAction")}
              data-testid="api-token-revoke-button"
              onClick={() => onRevoke(token)}
            >
              <DeleteIcon />
            </IconButton>
          </Line>
          <PermissionBadges permissions={token.permissions ?? []} />
          <Line>
            <Typography variant="caption" color="textSecondary" noWrap>
              {t("settings.apiAccess.table.lastUsed")}:{" "}
              {formatLastUsed(t, token.lastUsedAt)}
            </Typography>
            <TokenExpiryCell expiresAt={token.expiresAt} compact />
          </Line>
        </Row>
      ))}
    </Box>
  );
};
